import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { PackageErrorCodes } from "enum/error-codes/package-error-codes.enum";
import { ShipmentDutyOrTaxPaidBy } from "enum/shipment-duty-or-tax-paid-by.enum";
import { ShipmentExportReasons } from "enum/shipment-export-reasons.enum";
import {
  ShipmentContentsFieldNames,
  ShipmentDetailFieldNames,
} from "enum/shipment-field-names.enum";
import { ShipmentTypes } from "enum/shipment-types.enum";
import { IAddress } from "models/address/address.model";
import { IPickupPoint } from "models/pickupPoint/pickupPoint.model";
import { IQuotedRate } from "models/quote/quote.model";
import { ISalesAccount } from "models/salesAccount/salesAccount.model";
import {
  CarrierDetailList,
  IShipment,
  IShipmentItemDescription,
  LocationDetail,
} from "models/shipment/shipment.model";
import { IShipmentPackageGrouped } from "models/shipmentPackage/shipmentPackage.model";

export enum CreateShipmentAddressType {
  "ADDRESS_BOOK" = "ADDRESS_BOOK",
  "NEW" = "NEW",
  // Receiver only: the parcel goes to a carrier's locker or shop for collection.
  "PICKUP_POINT" = "PICKUP_POINT",
}

export type AddressOrigin = "sender" | "recipient" | "importer";

export type CreateAddress = Pick<
  IAddress,
  | "addressLine1"
  | "addressLine2"
  | "addressLine3"
  | "city"
  | "zip"
  | "company"
  | "countyState"
  | "countryIsoCode"
  | "email"
  | "phone"
  | "firstName"
  | "lastName"
  | "eoriNumber"
  | "vatNumber"
  | "isSenderDefault"
  | "residentialAddress"
>;

export type CreateShipmentAddress = CreateAddress & {
  id: IAddress["id"];
  /**
   * UUID of the address book entry this address was selected from, so the
   * entry can be updated when updateAddressBook is on. Null for new addresses.
   */
  uuid?: IAddress["uuid"] | null;
  saveToAddressBook: boolean;
  /**
   * When the user edits an address picked from the address book, this opt-in
   * flag writes the edits back to the address book entry on submit.
   */
  updateAddressBook?: boolean;
  editModeSavedAddress?: boolean;
  type: CreateShipmentAddressType;
  setImporterSwitch?: boolean;
  /**
   * The locker or shop the receiver collects from, when type is PICKUP_POINT.
   * The address fields then hold the point's address.
   */
  pickupPoint?: IPickupPoint | null;
};

export type CreateShipmentContents = Pick<
  IShipmentItemDescription,
  | ShipmentContentsFieldNames.commodityCode
  | ShipmentContentsFieldNames.countryOfOrigin
  | ShipmentContentsFieldNames.currency
  | ShipmentContentsFieldNames.description
  | ShipmentContentsFieldNames.itemValue
  | ShipmentContentsFieldNames.manufacturerDetails
  | ShipmentContentsFieldNames.manufacturerIdentifier
  | ShipmentContentsFieldNames.standardisedIdentifier
  | ShipmentContentsFieldNames.itemImageBase64
  | ShipmentContentsFieldNames.sku
  | ShipmentContentsFieldNames.weight
  | ShipmentContentsFieldNames.quantity
  | ShipmentContentsFieldNames.shippingMethod
> & { packageId: number };

export type CreateShipmentDetails = {
  [ShipmentDetailFieldNames.customerReference]: string;
  [ShipmentDetailFieldNames.customerReference2]: string;
  [ShipmentDetailFieldNames.reasonForExport]: ShipmentExportReasons | null;
  [ShipmentDetailFieldNames.dutyOrTaxPaidBy]: ShipmentDutyOrTaxPaidBy;
  [ShipmentDetailFieldNames.shouldSplitDutyAndVat]: boolean;
  [ShipmentDetailFieldNames.invoiceNumber]: string;
  [ShipmentDetailFieldNames.iossNumber]: string;
  [ShipmentDetailFieldNames.dangerousGoods]: string;
  [ShipmentDetailFieldNames.shipmentSourceName]: string;
  [ShipmentDetailFieldNames.integrationStoreID]: number;
  [ShipmentDetailFieldNames.shipmentIntegrationID]?: number;
  [ShipmentDetailFieldNames.orderNumber]: string;
  [ShipmentDetailFieldNames.UKIMSNumber]?: string;
  [ShipmentDetailFieldNames.integrationCreatedDate]?: string;
  [ShipmentDetailFieldNames.deliveryInstructions]?: string;
  [ShipmentDetailFieldNames.setImporterSwitch]?: boolean;
  [ShipmentDetailFieldNames.thirdPartyBillToAccountNumber]?: string;
};

export type ICreateShipmentSliceValues = {
  sender: CreateShipmentAddress;
  receiver: CreateShipmentAddress;
  importer: CreateShipmentAddress;
  details: CreateShipmentDetails;
  items: IShipmentPackageGrouped[];
  contents: CreateShipmentContents[];
};

export type DHLPickupCapabilitiesType = {
  nextBusinessDay?: boolean;
  localCutoffDateAndTime?: string;
  pickupEarliest?: string; // "HH:MM:SS"
  pickupLatest?: string; // "HH:MM:SS"
  pickupCutoffSameDayOutboundProcessing?: string;
};

// One DHL Express value added service ("special service"), priced for the selected
// rate's lane. serviceCode is DHL's 2-char global code, e.g. "WG" (Handover to Broker).
export type DHLValueAddedServiceOption = {
  serviceCode: string;
  name: string;
  // Minor units (pence/cents), sales price. null when DHL quoted no price: it only
  // prices a service once the shipment is re-rated with that service applied, so the
  // cost of an offered service is genuinely unknown until "Refresh the price" is used.
  amount: number | null;
  currency: string;
  serviceTypeCode?: string;
};

export type PickUpObjectType = {
  shouldPickUp: boolean;
  pickupDate: string;
  pickupFromTime: string;
  pickupToTime: string;
  pickupInstructions: string;
  dhlPickupCapabilities?: DHLPickupCapabilitiesType | null;
};
export type FoundDropOffLocationsDistance = {
  address: FoundDropOffLocations;
  distance: string;
  carrierDetailList?: CarrierDetailList[];
  locationDetail?: LocationDetail;
  geoLocation?: GeoLocation;
  // Carrier-neutral: the location's phone number and what kind of place it is
  // ("UPS Drop Box", "DHL Service Point"), filled in by each carrier's mapper.
  phone?: string;
  storeName?: string;
};
export type GeoLocation = {
  latitude: string;
  longitude: string;
};
/** DHL Express contact details, as its service point locator returns them. */
export type DHLServicePointContact = {
  phoneNumber?: string;
  email?: string;
  contactType?: string;
};

/** One drop-off point from DHL Express' service point locator (GSPL). */
export type DHLServicePoint = {
  facilityId?: string;
  servicePointName?: string;
  localName?: string;
  servicePointType?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    zipCode?: string;
    state?: string | null;
    country?: string;
  };
  geoLocation?: {
    latitude?: number;
    longitude?: number;
  };
  // Already formatted with its unit by DHL, e.g. "0.42 km" - the unit follows the
  // country searched (mi for GB/US, km elsewhere).
  distance?: string;
  shippingCutOffTime?: string;
  // DHL's spec is ambiguous about whether this is one object or a list, so the mapper
  // accepts both.
  contactDetails?: DHLServicePointContact | DHLServicePointContact[];
};

export type FoundDropOffLocations = {
  city: string;
  countryCode: string;
  postalCode: string;
  residential: boolean;
  stateOrProvinceCode: string;
  displayName: string;
};
export type DropoffObjectType = {
  shouldDropOff: boolean;
  dropOffLocations: FoundDropOffLocationsDistance[];
  selectedDropOffLocation: FoundDropOffLocations;
};

export type InsuranceType = {
  useInsurance: boolean;
  insuranceAmount: number;
  insuranceAmountCurrency: string;
};
export type SetDropOffPayload = {
  shouldDropOff: boolean;
  sender: CreateAddress;
  salesAccountUUID: ISalesAccount["uuid"] | null;
};

export type ExtraServicesType = {
  evriHouseHoldSignature: boolean;
  evriSignature: boolean;
};
export type ICreateShipmentSlice = {
  extraServices: ExtraServicesType;
  // DHL Express value added service codes the user picked for this shipment.
  dhlValueAddedServices: string[];
  insurance: InsuranceType;
  requiresSignature: boolean;
  requiresAdultSignature: boolean;
  requiresDirectDelivery: boolean;
  usedImportItemsAiFeature: boolean;
  requiresEmailNotification?: boolean;
  selectedNotificationEmail?: string | null;
  selectedNotificationEmails?: string[];
  isDisclaimerAccepted: boolean;
  shippingToResidence: boolean;
  saturdayDelivery: boolean;
  sundayDelivery: boolean;
  pickUpObject: PickUpObjectType;
  dropOffObject: DropoffObjectType;
  documentOption: { label: string; value: number };
  editingShipmentID: IShipment["id"] | null;
  editingShipmentUUID: IShipment["uuid"] | null;
  /**
   * The references the shipment being edited had when the form opened, so the
   * form can warn when a fulfillment-linked reference is changed. Null when
   * not editing (creating or duplicating).
   */
  editingOriginalReferences: {
    customerReference: string;
    customerReference2: string;
  } | null;
  /**
   * If quoting as an admin, the sales account UUID needs to be specified.
   * @default false
   */
  salesAccountUUID: ISalesAccount["uuid"] | null;
  files: { fileType: string; label: string; file: File | null }[];
  /**
   * Edit only: the types of the original shipment's uploaded documents (e.g.
   * "COMMERCIAL_INVOICE") that the edit keeps. The form shows them as already
   * uploaded and the server copies them onto the new shipment. Null when not
   * editing, or when the shipment was loaded without the list.
   */
  keptDocumentTypes: string[] | null;
  editingShipmentJobNumber: IShipment["jobNumber"] | null;
  printLater: boolean;
  isConvertingFromQuote: boolean;
  convertingFromQuoteStatus: "IDLE" | "LOADING" | "READY";
  values: ICreateShipmentSliceValues;
  type: ShipmentTypes;
  rates: IQuotedRate[];
  selectedRate: IQuotedRate | null;
  ratesError: string;
  ratesLoadingStatus: ApiLoadingStatus;
  submittingStatus: ApiLoadingStatus;
  submittingErrorMessage: string;
  submitCount: number;
  errors: CreateShipmentErrors;
  totalShipmentValue: null | number;
  touched: {
    sender: CreateShipmentAddressTouched;
    receiver: CreateShipmentAddressTouched;
    importer: CreateShipmentAddressTouched;
    items: { [key in keyof IShipmentPackageGrouped]: boolean }[];
    contents: { [key in keyof CreateShipmentContents]: boolean }[];
    details: { [key in keyof CreateShipmentDetails]: boolean };
  };
  currentQuoteReference: string;
  dangerousGoodsOption: { label: string; value: number };
  dangerousGoodsCategories: { label: string; value: number }[];
  duplicateOrEdit: string;
  currentQuoteReferenceIsValid: boolean;
  sendIntegrationUpdate: boolean;
  shipmentDateFromUI: string | null;
  // setImporterSwitch: boolean;
};

export type CreateShipmentAddressErrors = {
  [key in keyof CreateShipmentAddress]: string;
};

export type CreateShipmentAddressTouched = {
  [key in keyof Omit<
    CreateShipmentAddress,
    | "id"
    | "uuid"
    | "saveToAddressBook"
    | "updateAddressBook"
    | "type"
    | "pickupPoint"
  >]: boolean;
};

export type CreateShipmentItemErrors = {
  [key in keyof IShipmentPackageGrouped]: PackageErrorCodes | null;
};

export type CreateShipmentContentsErrors = {
  [key in keyof CreateShipmentContents]: string;
};

export type CreateShipmentDetailsErrors = {
  [key in keyof CreateShipmentDetails]: string;
};

export type CreateShipmentErrors = {
  sender: CreateShipmentAddressErrors;
  receiver: CreateShipmentAddressErrors;
  items: CreateShipmentItemErrors[];
  contents: CreateShipmentContentsErrors[];
  details: CreateShipmentDetailsErrors;
  itemAndPackage?: { itemAndPackageWeight: string }[];
};

/**
 * The drop-off endpoints only need the postal address, and the server validates
 * the body with forbidNonWhitelisted. Send exactly these fields rather than
 * spreading the whole sender - anything the address slice gains later (uuid,
 * updateAddressBook, ...) is rejected with a 400.
 */
export type DropOffAddress = Pick<
  CreateAddress,
  | "addressLine1"
  | "addressLine2"
  | "city"
  | "countyState"
  | "zip"
  | "countryIsoCode"
  | "isSenderDefault"
>;

export interface DropOffAddressWithServiceId extends DropOffAddress {
  serviceId: string;
}
export type CreateShipmentWeightOptions = {
  weightUnit?: string;
  divideTotalPackageWeightByQuantityInUi?: boolean;
};
